<template>
  <v-card class="seccion-card coverage-usage">
    <v-card-title class="section-head">
      <v-icon small>{{ icon }}</v-icon>{{ title }}
      <v-spacer />
      <slot name="actions" />
    </v-card-title>
    <p v-if="unlinkedCount" class="unlinked-note" data-test="unlinked-note">
      {{ unlinkedCount === 1 ? '1 uso no tiene' : `${unlinkedCount} usos no tienen` }} membresía asociada y no se
      muestran en ninguna membresía.
    </p>
    <v-alert
      v-for="notice in visibleNotices"
      :key="notice"
      type="warning"
      dense
      text
      class="mx-5 mt-3 mb-0"
    >{{ notice }}</v-alert>

    <div v-if="!isCompact" class="origin-filter" role="group" aria-label="Filtrar por origen">
      <span class="filter-label">Origen</span>
      <v-btn-toggle
        v-model="originFilter"
        mandatory
        dense
        color="primary"
        data-test="origin-filter"
      >
        <v-btn
          v-for="option in originFilterOptions"
          :key="option.value"
          small
          :value="option.value"
          :data-test="`origin-filter-${option.value}`"
        >{{ option.label }}</v-btn>
      </v-btn-toggle>
    </div>

    <v-data-table
      :headers="headersRequest"
      :items="tableRows"
      item-key="key"
      :items-per-page="isCompact ? limit : 10"
      :hide-default-footer="isCompact"
      :no-data-text="noDataText"
      sort-by="dateRank"
      sort-desc
      class="pdf-friendly-table pagination-right"
      :footer-props="{
        'items-per-page-options': [10],
        'show-items-per-page': false
      }"
    >
      <template v-slot:item.origin="{ item }">
        <v-chip-group v-if="originChip(item.origin)">
          <v-chip
            outlined
            small
            :color="originChip(item.origin).color"
            class="font-weight-bold"
          >
            {{ originChip(item.origin).label }}
          </v-chip>
        </v-chip-group>
      </template>

      <template v-slot:item.name="{ item }">
        <PetNameBadge
          v-if="item.name"
          :petName="item.name"
          :species="item.species"
        />
        <span v-else>-</span>
      </template>

      <template v-slot:item.service="{ item }">
        <span>{{ item.service || '-' }}</span>
      </template>

      <template v-slot:item.referenceIndex="{ item }">
        <router-link
          v-if="isRefundLink(item)"
          :to="refundRoute(item.referenceIndex)"
          class="refund-link"
          :data-test="`refund-link-${item.referenceIndex}`"
          target="_blank"
          rel="noopener"
        >{{ item.referenceIndex }}</router-link>
        <span v-else>{{ item.referenceIndex || '-' }}</span>
      </template>

      <template v-slot:item.dateRank="{ item }">
        <span>{{ item.createdAt }}</span>
      </template>

      <template v-slot:item.ces="{ item }">
        <span v-if="!hasCes(item.ces)">-</span>
        <span v-else class="ces-score" :class="cesTone(item.ces)">{{ item.ces }}</span>
      </template>

      <template v-slot:item.changelog="{ item }">
        <v-chip
          v-if="item.source === 'request'"
          small
          outlined
          :color="refundStatusColor(item.changelog)"
          class="font-weight-bold"
        >
          {{ item.changelog }}
        </v-chip>
        <span v-else>-</span>
      </template>

      <template v-slot:item.refundedAmount="{ item }">
        <span>{{ item.refundedAmount === null ? '-' : item.refundedAmount }}</span>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import PetNameBadge from './PetNameBadge.vue';
import { refundStatusColorsMixin } from '../mixins/refundStatusColorsMixin';
import { OPERATION_TYPES } from '../utils/services-record.utils';
import { refundDetailRoute } from '../utils/vision360.utils';

// Filtro por origen (113 US4): «Clínica» son las atenciones de Petmed; «Otros» junta el resto de
// los tipos de uso y los usos sin tipo, para que ninguna fila quede fuera de algún filtro.
const ORIGIN_FILTERS = {
  all: () => true,
  refund: (origin) => origin === 'refund',
  clinic: (origin) => origin === 'petmed',
  other: (origin) => origin !== 'refund' && origin !== 'petmed'
};

// Historial de usos de cobertura de la 360 (reembolsos y demás usos), extraído de Vision360Table.
export default {
  name: 'CoverageUsageTable',
  mixins: [refundStatusColorsMixin],
  components: { PetNameBadge },
  props: {
    // Reembolsos y demás usos ya unidos y ordenados (src/utils/coverage-usage.utils.js).
    coverageUsage: { type: Array, required: true },
    // Total de reembolsos que informa el servidor; solo sirve para avisar si no llegaron todos.
    refundsTotal: { type: Number, default: 0 },
    // Cada fuente de la tabla puede fallar por separado; la otra sigue visible y se avisa.
    refundsLoadFailed: { type: Boolean, default: false },
    usageLoadFailed: { type: Boolean, default: false },
    // Membresía elegida en la pestaña: la tabla muestra solo sus usos. null = todos.
    membershipId: { type: String, default: null },
    // Modo compacto (Resumen · «Últimos movimientos»): solo los `limit` usos más recientes,
    // sin filtro de origen, paginación ni avisos de carga.
    limit: { type: Number, default: null },
    title: { type: String, default: 'Uso de coberturas' },
    icon: { type: String, default: 'mdi-cash-refund' },
    noDataText: { type: String, default: 'Sin usos de cobertura registrados.' }
  },
  data() {
    return {
      originFilter: 'all',
      originFilterOptions: [
        { value: 'all', label: 'Todos' },
        { value: 'refund', label: 'Reembolsos' },
        { value: 'clinic', label: 'Clínica' },
        { value: 'other', label: 'Otros' }
      ],
      headersRequest: [
        { text: 'Origen', value: 'origin' },
        { text: 'Mascota', value: 'name' },
        { text: 'Servicio', value: 'service' },
        { text: 'N° reembolso', value: 'referenceIndex' },
        // Centrada: el cuadro de color de la nota y el "-" de "sin nota" quedan alineados entre sí.
        { text: 'CES', value: 'ces', align: 'center' },
        // Ordena por la fecha completa (dateRank) y no por el texto DD/MM/YY, que desordenaría los años.
        { text: 'Fecha solicitud', value: 'dateRank' },
        { text: 'Fecha cambio estado', value: 'statusChangedAt' },
        { text: 'Estado', value: 'changelog' },
        { text: 'Reembolso', value: 'refundedAmount' }
      ]
    };
  },
  created() {
    this.loadRefundStatusColors();
  },
  computed: {
    // Avisos de la sección: una lista parcial sin aviso se leería como si el usuario no tuviera
    // reembolsos u otros usos. Se calculan sobre todas las filas, no sobre las filtradas.
    coverageNotices() {
      const notices = [];
      if (this.refundsLoadFailed) notices.push('No se pudieron cargar los reembolsos.');
      if (this.usageLoadFailed) notices.push('No se pudieron cargar los demás usos de cobertura.');
      const refundRows = this.coverageUsage.filter(row => row.source === 'request').length;
      if (!this.refundsLoadFailed && this.refundsTotal > refundRows) {
        notices.push(`Se muestran los reembolsos más recientes (${refundRows} de ${this.refundsTotal}).`);
      }
      return notices;
    },
    membershipRows() {
      if (!this.membershipId) return this.coverageUsage;
      return this.coverageUsage.filter(row => row.membershipId === this.membershipId);
    },
    // Usos sin membresía (registros antiguos): con el filtro por membresía no aparecerían en ninguna.
    unlinkedCount() {
      if (!this.membershipId) return 0;
      return this.coverageUsage.filter(row => !row.membershipId).length;
    },
    filteredCoverageUsage() {
      const matches = ORIGIN_FILTERS[this.originFilter] || ORIGIN_FILTERS.all;
      return this.membershipRows.filter(row => matches(row.origin));
    },
    isCompact() {
      return Boolean(this.limit);
    },
    tableRows() {
      if (!this.isCompact) return this.filteredCoverageUsage;
      return [...this.membershipRows].sort((a, b) => (b.dateRank || 0) - (a.dateRank || 0)).slice(0, this.limit);
    },
    visibleNotices() {
      return this.isCompact ? [] : this.coverageNotices;
    }
  },
  methods: {
    // Solo los reembolsos con N° tienen ficha en la bandeja de reembolsos.
    isRefundLink(item) {
      return item.source === 'request' && Boolean(item.referenceIndex) && item.referenceIndex !== '-';
    },
    refundRoute: refundDetailRoute,
    // Texto y color del chip de origen, los mismos que "Egreso" en Registrar Servicio. Un uso
    // sin tipo, o con un tipo que no está en el mapa, no lleva chip.
    originChip(origin) {
      return Object.prototype.hasOwnProperty.call(OPERATION_TYPES, origin) ? OPERATION_TYPES[origin] : null;
    },
    // Sin nota (null, vacío o "-" que manda el historial) la celda muestra el mismo "-" plano que
    // el resto de los datos no disponibles de la tabla, no un cuadro ni otra raya.
    hasCes(ces) {
      return ces !== null && ces !== undefined && ces !== '' && ces !== '-';
    },
    // 4-5 conforme, 3 tibio, 1-2 mala experiencia.
    cesTone(ces) {
      const n = parseInt(ces, 10);
      if (Number.isNaN(n)) return 'is-neutral';
      if (n >= 4) return 'is-good';
      if (n === 3) return 'is-mid';
      return 'is-bad';
    }
  }
};
</script>

<style scoped>
.seccion-card {
  margin-bottom: 20px;
}

.section-head {
  font-size: 0.9375rem;
  font-weight: 600;
  padding: 15px 20px;
  border-bottom: 1px solid var(--pw-divider);
}
.section-head .v-icon {
  margin-right: 8px;
  color: var(--pw-text-muted);
}

.refund-link {
  font-weight: 600;
}

.unlinked-note {
  margin: 10px 20px 0;
  font-size: 0.78rem;
  color: var(--pw-text-muted);
}

.origin-filter {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 20px 4px;
}
.filter-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: var(--pw-text-faint);
}

/* El marco lo pone la tarjeta. */
.pdf-friendly-table {
  border: 0;
}

/* Nota CES como cuadrito de color: se escanea sin leer. */
.ces-score {
  display: inline-grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: var(--pw-radius-chip);
  font-size: 0.8125rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.ces-score.is-good    { background: var(--pw-success-tint); color: var(--pw-success); }
.ces-score.is-mid     { background: var(--pw-warning-tint); color: var(--pw-warning); }
.ces-score.is-bad     { background: var(--pw-error-tint);   color: var(--pw-error); }
.ces-score.is-neutral { background: var(--pw-divider);      color: var(--pw-text-muted); }

.pagination-right::v-deep .v-data-footer {
  justify-content: flex-end;
}
</style>
